import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Extract a displayable string from an API error, including FastAPI/pydantic
// validation errors where `detail` is an array of {msg, loc} objects.
export function getErrorMessage(error, fallback = 'Something went wrong') {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join(', ');
  }
  return fallback;
}

// Format currency in PKR
export function formatCurrency(amount, showSymbol = true) {
  if (amount === null || amount === undefined) return '-';
  const formatted = new Intl.NumberFormat('en-PK', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));
  
  if (showSymbol) {
    return `Rs. ${formatted}`;
  }
  return formatted;
}

// Format date
export function formatDate(dateString, includeTime = false) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const options = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  };
  
  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
  }
  
  return date.toLocaleDateString('en-PK', options);
}

// Format weight
export function formatWeight(weight) {
  if (weight === null || weight === undefined) return '-';
  return `${weight} kg`;
}

// Get initials from name
export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Check permission
export function hasPermission(user, permission) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[permission] === true;
}

// Permissions list
export const PERMISSIONS = [
  { key: 'manage_users', label: 'Manage Users' },
  { key: 'manage_staff', label: 'Manage Staff' },
  { key: 'manage_customers', label: 'Manage Customers' },
  { key: 'manage_vendor_partners', label: 'Manage Vendor Partners' },
  { key: 'manage_vendors', label: 'Manage Vendors' },
  { key: 'manage_rates', label: 'Manage Rates' },
  { key: 'create_bookings', label: 'Create Bookings' },
  { key: 'approve_bookings', label: 'Approve Bookings' },
  { key: 'manage_payments', label: 'Manage Payments' },
  { key: 'manage_expenses', label: 'Manage Expenses' },
  { key: 'view_reports', label: 'View Reports' },
  { key: 'view_ledgers', label: 'View Ledgers' },
];

// Expense categories
export const EXPENSE_CATEGORIES = [
  'Office Supplies',
  'Utilities',
  'Rent',
  'Salaries',
  'Transportation',
  'Maintenance',
  'Marketing',
  'Miscellaneous',
];

// What a savings entry is being set aside for
export const SAVINGS_CATEGORIES = [
  'Salaries',
  'Rent',
  'Bills & Utilities',
  'Taxes',
  'Emergency Fund',
  'General Savings',
];

// Where the parcel physically is - separate from the booking workflow status
export const DELIVERY_STATUSES = [
  'Booked',
  'On The Way',
  'Delay',
  'Clearance Info Required',
  'Delivery-Attempt',
  'Delivered',
  'Refused',
  'Returned To Shipper',
];

export const DELIVERY_STATUS_STYLES = {
  'Booked': 'bg-slate-100 text-slate-700 border-slate-200',
  'On The Way': 'bg-blue-100 text-blue-700 border-blue-200',
  'Delay': 'bg-amber-100 text-amber-700 border-amber-200',
  'Clearance Info Required': 'bg-orange-100 text-orange-700 border-orange-200',
  'Delivery-Attempt': 'bg-violet-100 text-violet-700 border-violet-200',
  'Delivered': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Refused': 'bg-red-100 text-red-700 border-red-200',
  'Returned To Shipper': 'bg-rose-100 text-rose-700 border-rose-200',
};

// Carrier-reported statuses are free text, so colour by the derived stage
// rather than the words themselves.
export const DELIVERY_STAGE_STYLES = DELIVERY_STATUS_STYLES;

// Payment methods
export const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'online', label: 'Online Transfer' },
  { value: 'cheque', label: 'Cheque' },
];

// Nature types
export const NATURE_TYPES = [
  { value: 'DOC', label: 'Document' },
  { value: 'N.DOC', label: 'Non-Document' },
];

// Countries (common destinations)
// Destination countries with their carrier zone.
// Zones drive rate lookup. They are carrier-specific - this table is the
// default (DHL); the Zone field on the consignment form stays editable so a
// different carrier's zone can be entered by hand.
export const COUNTRY_ZONES = [
  { code: 'AF', name: 'Afghanistan', zone: 10 },
  { code: 'AL', name: 'Albania', zone: 4 },
  { code: 'DZ', name: 'Algeria', zone: 10 },
  { code: 'AS', name: 'American Samoa', zone: 10 },
  { code: 'AD', name: 'Andorra', zone: 4 },
  { code: 'AO', name: 'Angola', zone: 10 },
  { code: 'AI', name: 'Anguilla', zone: 10 },
  { code: 'AG', name: 'Antigua', zone: 10 },
  { code: 'AR', name: 'Argentina', zone: 9 },
  { code: 'AM', name: 'Armenia', zone: 10 },
  { code: 'AW', name: 'Aruba', zone: 10 },
  { code: 'AU', name: 'Australia', zone: 11 },
  { code: 'AT', name: 'Austria', zone: 4 },
  { code: 'AZ', name: 'Azerbaijan', zone: 10 },
  { code: 'BS', name: 'Bahamas', zone: 10 },
  { code: 'BH', name: 'Bahrain', zone: 1 },
  { code: 'BD', name: 'Bangladesh', zone: 2 },
  { code: 'BB', name: 'Barbados', zone: 10 },
  { code: 'BY', name: 'Belarus', zone: 10 },
  { code: 'BE', name: 'Belgium', zone: 4 },
  { code: 'BZ', name: 'Belize', zone: 10 },
  { code: 'BJ', name: 'Benin', zone: 10 },
  { code: 'BM', name: 'Bermuda', zone: 10 },
  { code: 'BT', name: 'Bhutan', zone: 5 },
  { code: 'BO', name: 'Bolivia', zone: 9 },
  { code: 'XB', name: 'Bonaire', zone: 10 },
  { code: 'BA', name: 'Bosnia & Herzegovina', zone: 4 },
  { code: 'BW', name: 'Botswana', zone: 10 },
  { code: 'BR', name: 'Brazil', zone: 9 },
  { code: 'BN', name: 'Brunei', zone: 5 },
  { code: 'BG', name: 'Bulgaria', zone: 4 },
  { code: 'BF', name: 'Burkina Faso', zone: 10 },
  { code: 'BI', name: 'Burundi', zone: 10 },
  { code: 'KH', name: 'Cambodia', zone: 5 },
  { code: 'CM', name: 'Cameroon', zone: 10 },
  { code: 'CA', name: 'Canada', zone: 14 },
  { code: 'IC', name: 'Canary Islands, The', zone: 10 },
  { code: 'CV', name: 'Cape Verde', zone: 10 },
  { code: 'KY', name: 'Cayman Islands', zone: 10 },
  { code: 'CF', name: 'Central African Rep', zone: 10 },
  { code: 'TD', name: 'Chad', zone: 10 },
  { code: 'CL', name: 'Chile', zone: 9 },
  { code: 'CN', name: 'China', zone: 6 },
  { code: 'CO', name: 'Colombia', zone: 9 },
  { code: 'KM', name: 'Comoros', zone: 10 },
  { code: 'CG', name: 'Congo', zone: 10 },
  { code: 'CD', name: 'Congo, DPR', zone: 10 },
  { code: 'CK', name: 'Cook Islands', zone: 10 },
  { code: 'CR', name: 'Costa Rica', zone: 10 },
  { code: 'CI', name: 'Cote D Ivoire', zone: 10 },
  { code: 'HR', name: 'Croatia', zone: 4 },
  { code: 'CU', name: 'Cuba', zone: 10 },
  { code: 'XC', name: 'Curacao', zone: 10 },
  { code: 'CY', name: 'Cyprus', zone: 4 },
  { code: 'CZ', name: 'Czech Rep., The', zone: 4 },
  { code: 'DK', name: 'Denmark', zone: 4 },
  { code: 'DJ', name: 'Djibouti', zone: 10 },
  { code: 'DM', name: 'Dominica', zone: 10 },
  { code: 'DO', name: 'Dominican Rep.', zone: 10 },
  { code: 'EC', name: 'Ecuador', zone: 9 },
  { code: 'EG', name: 'Egypt', zone: 9 },
  { code: 'SV', name: 'El Salvador', zone: 10 },
  { code: 'ER', name: 'Eritrea', zone: 10 },
  { code: 'EE', name: 'Estonia', zone: 4 },
  { code: 'SZ', name: 'Eswatini', zone: 10 },
  { code: 'ET', name: 'Ethiopia', zone: 10 },
  { code: 'FK', name: 'Falkland Islands', zone: 10 },
  { code: 'FO', name: 'Faroe Islands', zone: 10 },
  { code: 'FJ', name: 'Fiji', zone: 10 },
  { code: 'FI', name: 'Finland', zone: 4 },
  { code: 'FR', name: 'France', zone: 4 },
  { code: 'GF', name: 'French Guyana', zone: 9 },
  { code: 'GA', name: 'Gabon', zone: 10 },
  { code: 'GM', name: 'Gambia', zone: 10 },
  { code: 'GE', name: 'Georgia', zone: 10 },
  { code: 'DE', name: 'Germany', zone: 4 },
  { code: 'GH', name: 'Ghana', zone: 10 },
  { code: 'GI', name: 'Gibraltar', zone: 4 },
  { code: 'GR', name: 'Greece', zone: 4 },
  { code: 'GL', name: 'Greenland', zone: 10 },
  { code: 'GD', name: 'Grenada', zone: 10 },
  { code: 'GP', name: 'Guadeloupe', zone: 10 },
  { code: 'GU', name: 'Guam', zone: 10 },
  { code: 'GT', name: 'Guatemala', zone: 10 },
  { code: 'GG', name: 'Guernsey', zone: 4 },
  { code: 'GN', name: 'Guinea Rep.', zone: 10 },
  { code: 'GW', name: 'Guinea-Bissau', zone: 10 },
  { code: 'GQ', name: 'Guinea-Equatorial', zone: 10 },
  { code: 'GY', name: 'Guyana (British)', zone: 10 },
  { code: 'HT', name: 'Haiti', zone: 10 },
  { code: 'HN', name: 'Honduras', zone: 10 },
  { code: 'HK', name: 'Hong Kong SAR China', zone: 6 },
  { code: 'HU', name: 'Hungary', zone: 4 },
  { code: 'IS', name: 'Iceland', zone: 4 },
  { code: 'IN', name: 'India', zone: 5 },
  { code: 'ID', name: 'Indonesia', zone: 6 },
  { code: 'IR', name: 'Iran', zone: 10 },
  { code: 'IQ', name: 'Iraq', zone: 10 },
  { code: 'IE', name: 'Ireland, Rep. Of', zone: 4 },
  { code: 'IT', name: 'Italy', zone: 4 },
  { code: 'JM', name: 'Jamaica', zone: 10 },
  { code: 'JP', name: 'Japan', zone: 6 },
  { code: 'JE', name: 'Jersey', zone: 4 },
  { code: 'JO', name: 'Jordan', zone: 2 },
  { code: 'KZ', name: 'Kazakhstan', zone: 10 },
  { code: 'KE', name: 'Kenya', zone: 10 },
  { code: 'KI', name: 'Kiribati', zone: 10 },
  { code: 'KP', name: 'Korea,  D.P.R Of', zone: 10 },
  { code: 'KR', name: 'Korea, Rep. Of', zone: 6 },
  { code: 'KV', name: 'Kosovo', zone: 4 },
  { code: 'KW', name: 'Kuwait', zone: 2 },
  { code: 'KG', name: 'Kyrgyzstan', zone: 10 },
  { code: 'LA', name: 'Laos', zone: 5 },
  { code: 'LV', name: 'Latvia', zone: 4 },
  { code: 'LB', name: 'Lebanon', zone: 10 },
  { code: 'LS', name: 'Lesotho', zone: 10 },
  { code: 'LR', name: 'Liberia', zone: 10 },
  { code: 'LY', name: 'Libya', zone: 10 },
  { code: 'LI', name: 'Liechtenstein', zone: 4 },
  { code: 'LT', name: 'Lithuania', zone: 4 },
  { code: 'LU', name: 'Luxembourg', zone: 4 },
  { code: 'MO', name: 'Macau SAR China', zone: 6 },
  { code: 'MG', name: 'Madagascar', zone: 10 },
  { code: 'MW', name: 'Malawi', zone: 10 },
  { code: 'MY', name: 'Malaysia', zone: 6 },
  { code: 'MV', name: 'Maldives', zone: 5 },
  { code: 'ML', name: 'Mali', zone: 10 },
  { code: 'MT', name: 'Malta', zone: 4 },
  { code: 'MH', name: 'Marshall Islands', zone: 10 },
  { code: 'MQ', name: 'Martinique', zone: 10 },
  { code: 'MR', name: 'Mauritania', zone: 10 },
  { code: 'MU', name: 'Mauritius', zone: 9 },
  { code: 'YT', name: 'Mayotte', zone: 10 },
  { code: 'MX', name: 'Mexico', zone: 9 },
  { code: 'FM', name: 'Micronesia', zone: 10 },
  { code: 'MD', name: 'Moldova, Rep. Of', zone: 10 },
  { code: 'MC', name: 'Monaco', zone: 4 },
  { code: 'MN', name: 'Mongolia', zone: 10 },
  { code: 'ME', name: 'Montenegro, Rep Of', zone: 4 },
  { code: 'MS', name: 'Montserrat', zone: 10 },
  { code: 'MA', name: 'Morocco', zone: 10 },
  { code: 'MZ', name: 'Mozambique', zone: 10 },
  { code: 'MM', name: 'Myanmar', zone: 5 },
  { code: 'NA', name: 'Namibia', zone: 10 },
  { code: 'NR', name: 'Nauru, Rep. Of', zone: 10 },
  { code: 'NP', name: 'Nepal', zone: 2 },
  { code: 'NL', name: 'Netherlands, The', zone: 4 },
  { code: 'XN', name: 'Nevis', zone: 10 },
  { code: 'NC', name: 'New Caledonia', zone: 10 },
  { code: 'NZ', name: 'New Zealand', zone: 7 },
  { code: 'NI', name: 'Nicaragua', zone: 10 },
  { code: 'NE', name: 'Niger', zone: 10 },
  { code: 'NG', name: 'Nigeria', zone: 9 },
  { code: 'NU', name: 'Niue', zone: 10 },
  { code: 'MK', name: 'North Macedonia', zone: 4 },
  { code: 'MP', name: 'Northern Mariana Islands', zone: 10 },
  { code: 'NO', name: 'Norway', zone: 4 },
  { code: 'OM', name: 'Oman', zone: 2 },
  { code: 'PW', name: 'Palau', zone: 10 },
  { code: 'PA', name: 'Panama', zone: 9 },
  { code: 'PG', name: 'Papua New Guinea', zone: 10 },
  { code: 'PY', name: 'Paraguay', zone: 9 },
  { code: 'PE', name: 'Peru', zone: 9 },
  { code: 'PH', name: 'Philippines, The', zone: 5 },
  { code: 'PL', name: 'Poland', zone: 4 },
  { code: 'PT', name: 'Portugal', zone: 4 },
  { code: 'PR', name: 'Puerto Rico', zone: 9 },
  { code: 'QA', name: 'Qatar', zone: 1 },
  { code: 'RE', name: 'Reunion, Island Of', zone: 10 },
  { code: 'RO', name: 'Romania', zone: 4 },
  { code: 'RU', name: 'Russian Federation', zone: 10 },
  { code: 'RW', name: 'Rwanda', zone: 10 },
  { code: 'SH', name: 'Saint Helena', zone: 10 },
  { code: 'WS', name: 'Samoa', zone: 10 },
  { code: 'SM', name: 'San Marino', zone: 4 },
  { code: 'ST', name: 'Sao Tome And Principe', zone: 10 },
  { code: 'SA', name: 'Saudi Arabia', zone: 13 },
  { code: 'SN', name: 'Senegal', zone: 10 },
  { code: 'RS', name: 'Serbia, Rep. Of', zone: 4 },
  { code: 'SC', name: 'Seychelles', zone: 10 },
  { code: 'SL', name: 'Sierra Leone', zone: 10 },
  { code: 'SG', name: 'Singapore', zone: 6 },
  { code: 'SK', name: 'Slovakia', zone: 4 },
  { code: 'SI', name: 'Slovenia', zone: 4 },
  { code: 'SB', name: 'Solomon Islands', zone: 10 },
  { code: 'XS', name: 'Somalia Hargeisa', zone: 10 },
  { code: 'SO', name: 'Somalia Mogadishu', zone: 10 },
  { code: 'ZA', name: 'South Africa', zone: 9 },
  { code: 'SS', name: 'South Sudan', zone: 10 },
  { code: 'ES', name: 'Spain', zone: 4 },
  { code: 'LK', name: 'Sri Lanka', zone: 2 },
  { code: 'XY', name: 'St. Barthelemy', zone: 10 },
  { code: 'XE', name: 'St. Eustatius', zone: 10 },
  { code: 'KN', name: 'St. Kitts', zone: 10 },
  { code: 'LC', name: 'St. Lucia', zone: 10 },
  { code: 'XM', name: 'St. Maarten', zone: 10 },
  { code: 'VC', name: 'St. Vincent', zone: 10 },
  { code: 'SD', name: 'Sudan', zone: 10 },
  { code: 'SR', name: 'Suriname', zone: 10 },
  { code: 'SE', name: 'Sweden', zone: 4 },
  { code: 'CH', name: 'Switzerland', zone: 4 },
  { code: 'SY', name: 'Syria', zone: 10 },
  { code: 'PF', name: 'Tahiti', zone: 10 },
  { code: 'TW', name: 'Taiwan', zone: 6 },
  { code: 'TJ', name: 'Tajikistan', zone: 10 },
  { code: 'TZ', name: 'Tanzania', zone: 10 },
  { code: 'TH', name: 'Thailand', zone: 6 },
  { code: 'TL', name: 'Timor-Leste', zone: 10 },
  { code: 'TG', name: 'Togo', zone: 10 },
  { code: 'TO', name: 'Tonga', zone: 10 },
  { code: 'TT', name: 'Trinidad And Tobago', zone: 10 },
  { code: 'TN', name: 'Tunisia', zone: 10 },
  { code: 'TR', name: 'Turkey', zone: 2 },
  { code: 'TM', name: 'Turkmenistan', zone: 10 },
  { code: 'TC', name: 'Turks & Caicos', zone: 10 },
  { code: 'TV', name: 'Tuvalu', zone: 10 },
  { code: 'UG', name: 'Uganda', zone: 10 },
  { code: 'UA', name: 'Ukraine', zone: 10 },
  { code: 'AE', name: 'United Arab Emirates', zone: 12 },
  { code: 'GB', name: 'United Kingdom', zone: 3 },
  { code: 'UY', name: 'Uruguay', zone: 9 },
  { code: 'US', name: 'USA', zone: 8 },
  { code: 'UZ', name: 'Uzbekistan', zone: 10 },
  { code: 'VU', name: 'Vanuatu', zone: 10 },
  { code: 'VA', name: 'Vatican City', zone: 4 },
  { code: 'VE', name: 'Venezuela', zone: 10 },
  { code: 'VN', name: 'Vietnam', zone: 6 },
  { code: 'VG', name: 'Virgin Islands-British', zone: 10 },
  { code: 'VI', name: 'Virgin Islands-US', zone: 10 },
  { code: 'YE', name: 'Yemen, Rep. Of', zone: 10 },
  { code: 'ZM', name: 'Zambia', zone: 10 },
  { code: 'ZW', name: 'Zimbabwe', zone: 10 },
];

// Plain code list, kept for anything that just needs the codes.
export const COUNTRIES = COUNTRY_ZONES.map((c) => c.code);

const ZONE_BY_CODE = Object.fromEntries(COUNTRY_ZONES.map((c) => [c.code, c.zone]));

export const zoneForCountry = (code) => ZONE_BY_CODE[code] ?? null;

export const countryLabel = (code) => {
  const c = COUNTRY_ZONES.find((x) => x.code === code);
  return c ? `${c.name} (${c.code})` : code;
};
